import React, { useEffect, useState } from "react";
import {
    Cpu,
    MemoryStick,
    HardDrive,
    Monitor,
    Zap,
    ChevronDown,
    ChevronUp,
    RefreshCw
} from "lucide-react";
import baseapi from "../../../services/baseapi";
import { showError } from "../../../utils/toast";

// Util for badge color
function getHealthStatusColor(status: string) {
    if (status === "OK" || status === "Good") return "bg-green-100 text-green-700 border border-green-200 dark:border-transparent dark:bg-green-600/30 dark:text-green-400";
    if (status === "Warning") return "bg-yellow-100 text-yellow-800 border border-yellow-200 dark:border-transparent dark:bg-yellow-600/30 dark:text-yellow-400";
    if (status === "Error" || status === "Bad") return "bg-red-100 text-red-700 border border-red-200 dark:border-transparent dark:bg-red-600/30 dark:text-red-400";
    return "bg-slate-100 text-slate-700 border border-slate-200 dark:border-transparent dark:bg-gray-600/30 dark:text-gray-200";
}

interface HardwareInfoProps {
    agent_id: string;
    organization_id: string;
    asset_id: string;
    client_id: string;
}

const HardwareInfo: React.FC<HardwareInfoProps> = ({
    agent_id,
    organization_id,
    asset_id,
    client_id,
}) => {
    const [hardware, setHardware] = useState<any>(null);
    const [memory, setMemory] = useState<any>(null);
    const [storage, setStorage] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['memory-modules']));

    async function fetchAll() {
        setLoading(true);
        try {
            const params = {
                query: JSON.stringify({
                    client_id,
                    organization_id,
                    agent_id,
                    asset_id
                }),
            };
            const [hw, mem, stor] = await Promise.all([
                baseapi.get("/asset_hardware", { params }),
                baseapi.get("/asset_memory", { params }),
                baseapi.get("/asset_storage", { params }),
            ]);
            setHardware(hw.data?.data?.[0] ?? null);
            setMemory(mem.data?.data?.[0] ?? null);
            setStorage(stor.data?.data ?? null);
        } catch (e: any) {
            showError(e.message);
        }
        setLoading(false);
    }
    useEffect(() => {
        fetchAll();
    }, [agent_id, asset_id, organization_id, client_id]);

    if (loading) {
        return (
            <div className="flex items-center justify-center p-8 space-x-3 text-slate-500 dark:text-gray-400 text-xs">
                <div className="w-5 h-5 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin"></div>
                <span>Loading hardware information...</span>
            </div>
        );
    }

    const safeParse = (value: any, fallback: any = []) => {
        try {
            if (!value) return fallback;
            return typeof value === "string" ? JSON.parse(value) : value;
        } catch {
            return fallback;
        }
    };

    const cpuList = safeParse(hardware?.cpu, []);
    const memoryModules = safeParse(memory?.modules, []);

    const storageList: any[] = Array.isArray(storage)
        ? storage
        : (storage && typeof storage === 'object' && Array.isArray(storage.data) ? storage.data : [storage]);

    const validStorageItems = storageList.filter((item: any) =>
        item && typeof item === 'object' && (
            item.total_gb !== null ||
            item.used_gb !== null ||
            item.available_gb !== null ||
            item.device !== null ||
            item.path !== null ||
            item.filesystem !== null
        )
    );

    const totalStorageGb = validStorageItems.reduce((acc: number, item: any) => acc + (Number(item.total_gb) || 0), 0);
    const totalUsedGb = validStorageItems.reduce((acc: number, item: any) => acc + (Number(item.used_gb) || 0), 0);
    const totalAvailableGb = validStorageItems.reduce((acc: number, item: any) => acc + (Number(item.available_gb) || 0), 0);

    const firstItemWithResults = storageList.find((item: any) => item?.results);
    const parsedResults = safeParse(firstItemWithResults?.results || (storage && !Array.isArray(storage) ? storage?.results : {}), {});
    const physicalDrives = safeParse(parsedResults.physical_drives, []);
    let logicalDrives = safeParse(parsedResults.logical_drives, []);

    const audioDevices = safeParse(hardware?.audio, []);
    const graphicsDevices = safeParse(hardware?.graphics, []);

    // Collapse/expand toggles
    const toggleSection = (section: string) => {
        setExpandedSections((prev) => {
            const next = new Set(prev);
            if (next.has(section)) next.delete(section);
            else next.add(section);
            return next;
        });
    };

    return (
        <div className="p-3 text-slate-800 dark:text-gray-200">
            <div className="flex items-center justify-between mb-3 border-b border-slate-200 dark:border-gray-700 pb-2">
                <div className="flex items-center">
                    <div className="p-1.5 bg-orange-600 rounded mr-2.5">
                        <Cpu className="w-4 h-4 text-white" />
                    </div>
                    <h2 className="text-sm font-bold text-slate-900 dark:text-white leading-tight">Hardware Information</h2>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => fetchAll()}
                        disabled={loading}
                        className="p-1.5 bg-white dark:bg-gray-800 hover:bg-slate-50 dark:hover:bg-gray-700 text-slate-600 dark:text-gray-200 hover:text-slate-900 dark:hover:text-white border border-slate-200 dark:border-gray-600 rounded-md transition-colors disabled:opacity-50 shadow-sm cursor-pointer"
                        title="Refresh data"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-orange-500' : ''}`} />
                    </button>
                </div>
            </div>

            <div className="space-y-3">
                {/* Top Row: CPU & Memory Side-by-Side */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {/* CPU Section */}
                    <div className="bg-white dark:bg-gray-900/50 p-4 rounded-lg border border-slate-200 dark:border-gray-600/30 flex flex-col justify-between h-full shadow-sm dark:shadow-none">
                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider flex items-center">
                                    <Cpu className="w-4 h-4 mr-2 text-orange-500" />
                                    CPU Information ({cpuList.length})
                                </h3>
                                {cpuList.length > 1 && (
                                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-500/20">
                                        Multi-Processor ({cpuList.length})
                                    </span>
                                )}
                            </div>

                            {cpuList.length > 0 ? (
                                <div className={`grid gap-2.5 ${cpuList.length > 1 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"}`}>
                                    {cpuList.map((cpu: any, idx: number) => (
                                        <div
                                            key={idx}
                                            className={`rounded-md text-xs ${
                                                cpuList.length > 1
                                                    ? "bg-slate-50/70 dark:bg-gray-800/40 border border-slate-200 dark:border-gray-700/60 p-2.5 flex flex-col justify-between"
                                                    : "bg-transparent p-0"
                                            }`}
                                        >
                                            <div className="flex items-center justify-between mb-2 pb-1.5 border-b border-slate-200 dark:border-gray-700/50 min-w-0">
                                                <span className="font-bold text-blue-700 dark:text-blue-300 text-xs flex items-center gap-1.5 min-w-0 truncate" title={cpu?.name || "-"}>
                                                    <Cpu className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400 shrink-0" />
                                                    <span className="truncate">{cpu?.name || `Processor #${idx + 1}`} {cpu.socketDesignation ? `(${cpu.socketDesignation})` : ""}</span>
                                                </span>
                                                {cpu.status && (
                                                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold shrink-0 ${getHealthStatusColor(cpu.status)}`}>
                                                        {cpu.status}
                                                    </span>
                                                )}
                                            </div>

                                            <div className="grid grid-cols-2 gap-1.5 text-xs">
                                                <div>
                                                    <p className="text-[10px] text-slate-500 dark:text-gray-400 uppercase font-medium">Current Usage</p>
                                                    <p className="text-blue-600 dark:text-blue-400 font-bold text-xs">
                                                        {cpu?.currentUsage || "-"}
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="text-[10px] text-slate-500 dark:text-gray-400 uppercase font-medium">Cores / Threads</p>
                                                    <p className="text-slate-900 dark:text-white text-xs">
                                                        {cpu?.cores ?? "-"} Cores / {cpu?.logicalProcessors ?? "-"} Threads
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="text-[10px] text-slate-500 dark:text-gray-400 uppercase font-medium">Clock Speed</p>
                                                    <p className="text-slate-900 dark:text-white text-xs font-mono truncate" title={cpu?.currentClockSpeed || cpu?.maxClockSpeed}>
                                                        {cpu?.currentClockSpeed || cpu?.maxClockSpeed || "-"}
                                                    </p>
                                                </div>
                                                {(cpu?.l2CacheSize || cpu?.l3CacheSize) && (
                                                    <div>
                                                        <p className="text-[10px] text-slate-500 dark:text-gray-400 uppercase font-medium">Cache (L2 / L3)</p>
                                                        <p className="text-slate-700 dark:text-gray-300 text-xs font-mono truncate">
                                                            {cpu?.l2CacheSize || '-'} / {cpu?.l3CacheSize || '-'}
                                                        </p>
                                                    </div>
                                                )}
                                                {cpu?.manufacturer && (
                                                    <div>
                                                        <p className="text-[10px] text-slate-500 dark:text-gray-400 uppercase font-medium">Manufacturer</p>
                                                        <p className="text-slate-700 dark:text-gray-300 text-xs font-mono truncate">
                                                            {cpu?.manufacturer}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-slate-500 dark:text-gray-400 text-xs">No CPU information available</p>
                            )}
                        </div>
                    </div>

                    {/* Memory Section */}
                    <div className="bg-white dark:bg-gray-900/50 p-4 rounded-lg border border-slate-200 dark:border-gray-600/30 flex flex-col justify-between shadow-sm dark:shadow-none">
                        <div>
                            <h3 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider mb-3 flex items-center">
                                <MemoryStick className="w-4 h-4 mr-2 text-orange-500" />
                                Memory Information
                            </h3>
                            <div className="grid grid-cols-3 gap-3 mb-3">
                                <div>
                                    <p className="text-[11px] text-slate-500 dark:text-gray-200 uppercase font-medium">Total Memory</p>
                                    <p className="text-slate-900 dark:text-white font-bold text-sm">
                                        {memory?.total_ram || "-"}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-[11px] text-slate-500 dark:text-gray-200 uppercase font-medium">Used Memory</p>
                                    <p className="text-purple-600 dark:text-purple-400 font-bold text-sm">
                                        {memory?.used_ram || "-"}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-[11px] text-slate-500 dark:text-gray-200 uppercase font-medium">Available Memory</p>
                                    <p className="text-green-600 dark:text-green-400 font-bold text-sm">
                                        {memory?.available_ram || "-"}
                                    </p>
                                </div>
                            </div>
                        </div>
                        {memoryModules.length > 0 && (
                            <div>
                                <button
                                    onClick={() => toggleSection('memory-modules')}
                                    className="flex items-center space-x-2 text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 mb-1 text-xs font-semibold cursor-pointer"
                                >
                                    {expandedSections.has('memory-modules') ?
                                        <ChevronUp className="w-3 h-3" /> :
                                        <ChevronDown className="w-3 h-3" />
                                    }
                                    <span>
                                        Show Memory Modules ({memoryModules.length})
                                    </span>
                                </button>
                                {expandedSections.has('memory-modules') && (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
                                        {memoryModules.map((module: any, idx: number) => (
                                            <div key={idx} className="bg-slate-50/70 dark:bg-gray-800/50 p-2.5 rounded border border-slate-200 dark:border-gray-700/50 shadow-sm dark:shadow-none">
                                                <p className="text-slate-900 dark:text-white font-semibold text-xs mb-1">{module.device_locator || module.bank_label || 'Unknown'}</p>
                                                <div className="grid grid-cols-2 gap-y-0.5 text-[10px] text-slate-500 dark:text-gray-200">
                                                    <p>Capacity: <span className="text-slate-800 dark:text-white font-medium">{module?.capacity_gb || "-"} GB</span></p>
                                                    <p>Speed: <span className="text-slate-800 dark:text-white font-medium">{module?.speed || module?.configured_clock_speed || "-"} MHz</span></p>
                                                    <p className="truncate">Manufacturer: <span className="text-slate-800 dark:text-white font-medium">{module?.manufacturer || "-"}</span></p>
                                                    <p className="truncate">Part: <span className="text-slate-800 dark:text-white font-mono">{module?.part_number || module?.partNumber || "-"}</span></p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Middle Row: Full-Width Storage Section */}
                <div className="bg-white dark:bg-gray-900/50 p-4 rounded-lg border border-slate-200 dark:border-gray-600/30 shadow-sm dark:shadow-none">
                    <h3 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider mb-3 flex items-center">
                        <HardDrive className="w-4 h-4 mr-2 text-orange-500" />
                        Storage Information
                    </h3>

                    {/* Storage Summary Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                        <div className="min-w-0 bg-slate-50/70 dark:bg-gray-800/40 p-3 rounded-lg border border-slate-200 dark:border-gray-700/50 shadow-sm dark:shadow-none">
                            <p className="text-[11px] text-slate-500 dark:text-gray-200 uppercase font-medium">Total Storage</p>
                            <p className="text-slate-900 dark:text-white font-bold text-sm">
                                {validStorageItems.length > 0
                                    ? `${totalStorageGb.toFixed(2)} GB`
                                    : (parsedResults?.total_gb ? `${parsedResults.total_gb} GB` : "-")}
                            </p>
                        </div>
                        <div className="min-w-0 bg-slate-50/70 dark:bg-gray-800/40 p-3 rounded-lg border border-slate-200 dark:border-gray-700/50 shadow-sm dark:shadow-none">
                            <p className="text-[11px] text-slate-500 dark:text-gray-200 uppercase font-medium">Used Storage</p>
                            <p className="text-amber-600 dark:text-yellow-400 font-bold text-sm">
                                {validStorageItems.length > 0
                                    ? `${totalUsedGb.toFixed(2)} GB`
                                    : (parsedResults?.used_gb ? `${parsedResults.used_gb} GB` : "-")}
                            </p>
                        </div>
                        <div className="min-w-0 bg-slate-50/70 dark:bg-gray-800/40 p-3 rounded-lg border border-slate-200 dark:border-gray-700/50 shadow-sm dark:shadow-none">
                            <p className="text-[11px] text-slate-500 dark:text-gray-200 uppercase font-medium">Available Storage</p>
                            <p className="text-emerald-600 dark:text-green-400 font-bold text-sm">
                                {validStorageItems.length > 0
                                    ? `${totalAvailableGb.toFixed(2)} GB`
                                    : (parsedResults?.available_gb ? `${parsedResults.available_gb} GB` : "-")}
                            </p>
                        </div>
                    </div>

                    {/* Direct Storage Items / Partitions List (3 columns grid on large screens) */}
                    {validStorageItems.length > 0 && (
                        <div className="space-y-2 mb-4">
                            <h4 className="text-xs font-bold text-slate-800 dark:text-gray-200 uppercase tracking-tight">Storage Devices & Partitions ({validStorageItems.length})</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {validStorageItems.map((item: any, idx: number) => {
                                    const usagePercent = item.used_percent !== null && item.used_percent !== undefined
                                        ? Number(item.used_percent)
                                        : (item.total_gb && item.used_gb ? Math.round((item.used_gb / item.total_gb) * 100) : 0);

                                    const healthStatus = usagePercent >= 90 ? "Bad" : (usagePercent >= 75 ? "Warning" : "Good");

                                    return (
                                        <div key={item.id || idx} className="bg-slate-50/70 dark:bg-gray-800/50 p-3 rounded-lg border border-slate-200 dark:border-gray-700/60 shadow-sm space-y-2">
                                            <div className="flex items-center justify-between gap-2">
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-slate-900 dark:text-white font-bold text-xs truncate" title={item.device || item.path || 'Disk'}>
                                                        {item.device || 'Disk Device'}
                                                    </p>
                                                    {item.path && (
                                                        <p className="text-[10px] text-slate-500 dark:text-gray-200 font-mono truncate" title={item.path}>
                                                            Mount: <span className="text-slate-700 dark:text-gray-200">{item.path}</span>
                                                        </p>
                                                    )}
                                                </div>
                                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${getHealthStatusColor(healthStatus)}`}>
                                                    {usagePercent}% Used
                                                </span>
                                            </div>

                                            {/* Usage Bar */}
                                            <div className="w-full bg-slate-200 dark:bg-gray-700/60 rounded-full h-1.5 overflow-hidden">
                                                <div
                                                    className={`h-full rounded-full transition-all duration-300 ${usagePercent >= 90 ? 'bg-red-500' : (usagePercent >= 75 ? 'bg-amber-500 dark:bg-yellow-500' : 'bg-emerald-500 dark:bg-green-500')
                                                        }`}
                                                    style={{ width: `${Math.min(100, Math.max(0, usagePercent))}%` }}
                                                />
                                            </div>

                                            <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-[10px] text-slate-500 dark:text-gray-200 pt-1 border-t border-slate-200 dark:border-gray-700/40">
                                                <div>Total: <span className="text-slate-900 dark:text-white font-semibold">{item.total_gb !== null && item.total_gb !== undefined ? `${item.total_gb} GB` : "-"}</span></div>
                                                <div>Used: <span className="text-amber-600 dark:text-yellow-400 font-semibold">{item.used_gb !== null && item.used_gb !== undefined ? `${item.used_gb} GB` : "-"}</span></div>
                                                <div>Free: <span className="text-emerald-600 dark:text-green-400 font-semibold">{item.available_gb !== null && item.available_gb !== undefined ? `${item.available_gb} GB` : "-"}</span></div>
                                                <div>FS: <span className="text-slate-700 dark:text-gray-200 font-mono">{item.filesystem || "-"}</span></div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {logicalDrives.length > 0 && (
                        <div className="mb-4">
                            <h4 className="text-xs font-bold text-slate-800 dark:text-gray-200 uppercase tracking-tight mb-2">Logical Drives</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                {logicalDrives.map((disk: any, idx: number) => (
                                    <div key={idx} className="bg-slate-50/70 dark:bg-gray-800/50 p-2.5 rounded border border-slate-200 dark:border-gray-700/50 shadow-sm dark:shadow-none">
                                        <div className="flex items-center justify-between mb-1.5">
                                            <span className="text-slate-900 dark:text-white font-semibold text-xs truncate max-w-[70%]">
                                                {disk.device_id || 'Unknown'} {disk.volume_name ? `(${disk.volume_name})` : ""}
                                            </span>
                                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${getHealthStatusColor(disk.usage_percentage >= 95 ? "Warning" : "OK")}`}>
                                                {disk.usage_percentage ?? '--'}%
                                            </span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-y-0.5 text-[10px] text-slate-500 dark:text-gray-200">
                                            <p>Total: <span className="text-slate-900 dark:text-white font-medium">{disk.total_size_gb} GB</span></p>
                                            <p>Used: <span className="text-slate-900 dark:text-white font-medium">{disk.used_space_gb} GB</span></p>
                                            <p>Free: <span className="text-slate-900 dark:text-white font-medium">{disk.free_space_gb} GB</span></p>
                                            <p>FS: <span className="text-slate-900 dark:text-white font-medium">{disk.file_system}</span></p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {physicalDrives.length > 0 && (
                        <>
                            <button
                                onClick={() => toggleSection('physical-disks')}
                                className="flex items-center space-x-2 text-amber-600 dark:text-yellow-400 hover:text-amber-700 dark:hover:text-yellow-300 mb-2 text-xs font-semibold cursor-pointer"
                            >
                                {expandedSections.has('physical-disks') ?
                                    <ChevronUp className="w-3 h-3" /> :
                                    <ChevronDown className="w-3 h-3" />
                                }
                                <span>
                                    Show Physical Disks ({physicalDrives.length})
                                </span>
                            </button>
                            {expandedSections.has('physical-disks') && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
                                    {physicalDrives.map((disk: any, idx: number) => (
                                        <div key={idx} className="bg-slate-50/70 dark:bg-gray-800/50 p-2.5 rounded border border-slate-200 dark:border-gray-700/50 flex justify-between items-center text-[10px] shadow-sm dark:shadow-none">
                                            <p className="text-slate-900 dark:text-white font-semibold truncate max-w-[50%]">{disk.model || 'Unknown Model'}</p>
                                            <p className="text-slate-500 dark:text-gray-200"><span className="text-slate-900 dark:text-white font-medium">{disk.size_gb} GB</span> • {disk.interface_type} • <span className="text-emerald-600 dark:text-green-400 font-semibold">{disk.status}</span></p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Bottom Row: Graphics & Audio Side-by-Side */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {/* Graphics Section */}
                    <div className="bg-white dark:bg-gray-900/50 p-4 rounded-lg border border-slate-200 dark:border-gray-600/30 shadow-sm dark:shadow-none">
                        <h3 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider mb-3 flex items-center">
                            <Monitor className="w-4 h-4 mr-2 text-orange-500" />
                            Graphics
                        </h3>
                        {graphicsDevices.length > 0 ? (
                            <div className="space-y-2.5">
                                {graphicsDevices.map((gpu: any, idx: number) => {
                                    const memory = gpu.adapterRAM !== undefined && gpu.adapterRAM !== "Unknown" && gpu.adapterRAM !== ""
                                        ? gpu.adapterRAM
                                        : (gpu.memory_mb !== undefined
                                            ? (gpu.memory_mb > 0 ? (gpu.memory_mb / 1024).toFixed(1) + ' GB' : 'Shared / -')
                                            : null);
                                    const driverVer = gpu.driverVersion || gpu.driver;
                                    const driverDateStr = gpu.driverDate
                                        ? (gpu.driverDate.includes('T') ? gpu.driverDate.split('T')[0] : gpu.driverDate)
                                        : gpu.driver_date;
                                    const processor = gpu.videoProcessor || gpu.video_processor;

                                    return (
                                        <div key={idx} className="bg-slate-50/70 dark:bg-gray-800/50 p-3 rounded-lg border border-slate-200 dark:border-gray-700/50 shadow-sm dark:shadow-none">
                                            <div className="flex justify-between items-start mb-2 pb-2 border-b border-slate-200 dark:border-gray-700/40">
                                                <p className="text-slate-900 dark:text-white font-semibold text-xs truncate max-w-[70%]" title={gpu.name}>{gpu.name || 'Unknown GPU'}</p>
                                                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${getHealthStatusColor(gpu.status || 'OK')}`}>
                                                    {gpu.status || 'Unknown'}
                                                </span>
                                            </div>
                                            <div className="grid grid-cols-2 gap-y-1.5 gap-x-2 text-[10px] text-slate-600 dark:text-gray-300 mt-1">
                                                {gpu.vendor && <p><span className="text-slate-500 dark:text-gray-400 font-medium">Vendor:</span> <span className="text-slate-900 dark:text-white font-medium">{gpu.vendor}</span></p>}
                                                {memory && <p><span className="text-slate-500 dark:text-gray-400 font-medium">Adapter RAM:</span> <span className="text-slate-900 dark:text-white font-medium">{memory}</span></p>}
                                                {driverVer && <p><span className="text-slate-500 dark:text-gray-400 font-medium">Driver Ver:</span> <span className="text-slate-900 dark:text-white font-mono">{driverVer}</span></p>}
                                                {driverDateStr && <p><span className="text-slate-500 dark:text-gray-400 font-medium">Driver Date:</span> <span className="text-slate-900 dark:text-white font-mono">{driverDateStr}</span></p>}
                                                {processor && <p className="col-span-2"><span className="text-slate-500 dark:text-gray-400 font-medium">Video Processor:</span> <span className="text-slate-900 dark:text-white font-medium">{processor}</span></p>}
                                                {gpu.temperature_c !== undefined && gpu.temperature_c > 0 && <p><span className="text-slate-500 dark:text-gray-400 font-medium">Temp:</span> <span className="text-orange-500 dark:text-orange-400 font-medium">{gpu.temperature_c}°C</span></p>}
                                                {gpu.utilization_percent !== undefined && <p><span className="text-slate-500 dark:text-gray-400 font-medium">Usage:</span> <span className="text-slate-900 dark:text-white font-medium">{gpu.utilization_percent}%</span></p>}
                                                {gpu.pci_address && <p className="col-span-2"><span className="text-slate-500 dark:text-gray-400 font-medium">PCI:</span> <span className="text-slate-900 dark:text-white font-mono">{gpu.pci_address}</span></p>}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <p className="text-slate-500 dark:text-gray-200 text-xs">No graphics information available</p>
                        )}
                    </div>

                    {/* Audio Section */}
                    <div className="bg-white dark:bg-gray-900/50 p-4 rounded-lg border border-slate-200 dark:border-gray-600/30 shadow-sm dark:shadow-none">
                        <h3 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider mb-3 flex items-center">
                            <Zap className="w-4 h-4 mr-2 text-orange-500" />
                            Audio
                        </h3>
                        {audioDevices.length > 0 ? (
                            <div className="space-y-1.5">
                                {audioDevices.map((audio: any, idx: number) => (
                                    <div key={idx} className="bg-slate-50/70 dark:bg-gray-800/50 p-2 rounded border border-slate-200 dark:border-gray-700/50 flex justify-between items-center shadow-sm dark:shadow-none">
                                        <p className="text-slate-900 dark:text-white font-semibold text-[10px] truncate max-w-[70%]">{audio.name || 'Unknown Audio Device'}</p>
                                        <span className="text-emerald-600 dark:text-green-400 text-[9px] uppercase font-bold">{audio.status}</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-slate-500 dark:text-gray-200 text-xs">No audio information available</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HardwareInfo;
